import { chakra, Spinner, Box } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import type { UseWaitForTransactionReceiptReturnType } from "wagmi";
import { useWaitForTransactionReceipt } from "wagmi";

import type { FormSubmitResultWalletClient } from "../types";

import config from "configs/app";
import { publicClient } from "lib/web3/client";
import { route } from "nextjs-routes";
import { Alert } from "toolkit/chakra/alert";
import { Link } from "toolkit/chakra/link";

const APP_CHAIN_ID = Number(config.chain.id);

interface Props {
  data: FormSubmitResultWalletClient["data"];
  onSettle: () => void;
}

const ContractMethodResultWalletClient = ({ data, onSettle }: Props) => {
  const txHash =
    data && "hash" in data ? (data.hash as `0x${string}`) : undefined;
  const chainId = data && "hash" in data ? data.chainId : undefined;
  const wagmiTxInfo = useWaitForTransactionReceipt({
    hash: txHash,
    chainId,
  });

  const useAppRpc = Boolean(
    txHash &&
    publicClient &&
    chainId != null &&
    Number(chainId) === APP_CHAIN_ID,
  );
  const receiptQuery = useQuery({
    queryKey: ["tx-receipt", txHash],
    queryFn: () => publicClient!.getTransactionReceipt({ hash: txHash! }),
    enabled: useAppRpc,
    refetchInterval: (query) => (query.state.data == null ? 2000 : false),
  });

  const txInfo = React.useMemo((): UseWaitForTransactionReceiptReturnType => {
    if (useAppRpc && receiptQuery.data) {
      const receipt = receiptQuery.data;
      return {
        ...wagmiTxInfo,
        status: receipt.status === "success" ? "success" : "error",
        data: receipt as UseWaitForTransactionReceiptReturnType["data"],
        error:
          receipt.status === "reverted"
            ? (wagmiTxInfo.error ?? {
                message: "Transaction reverted",
                name: "TransactionReverted",
              })
            : null,
      } as UseWaitForTransactionReceiptReturnType;
    }
    return wagmiTxInfo;
  }, [useAppRpc, receiptQuery.data, wagmiTxInfo]);

  return (
    <ContractMethodResultWalletClientDumb
      data={data}
      onSettle={onSettle}
      txInfo={txInfo}
    />
  );
};

export interface PropsDumb {
  data: FormSubmitResultWalletClient["data"];
  onSettle: () => void;
  txInfo: UseWaitForTransactionReceiptReturnType;
}

export const ContractMethodResultWalletClientDumb = ({
  data,
  onSettle,
  txInfo,
}: PropsDumb) => {
  const txHash = data && "hash" in data ? data.hash : undefined;

  React.useEffect(() => {
    if (txInfo.status !== "pending") {
      onSettle();
    }
  }, [onSettle, txInfo.status]);

  if (!data) {
    return null;
  }

  const isErrorResult = "message" in data;

  const txLink = txHash ? (
    <Link href={route({ pathname: "/tx/[hash]", query: { hash: txHash } })}>
      View transaction details
    </Link>
  ) : null;

  const content = (() => {
    if (isErrorResult) {
      return (
        <Alert status="error" textStyle="sm">
          {data.message}
        </Alert>
      );
    }

    switch (txInfo.status) {
      case "success": {
        return (
          <>
            <span>Transaction has been confirmed. </span>
            {txLink}
          </>
        );
      }

      case "pending": {
        return (
          <>
            <Spinner size="sm" mr={3} />
            <chakra.span verticalAlign="text-bottom">
              {"Waiting for transaction's confirmation. "}
              {txLink}
            </chakra.span>
          </>
        );
      }

      case "error": {
        return (
          <Alert
            status="error"
            textStyle="sm"
            descriptionProps={{
              flexDir: "column",
              alignItems: "flex-start",
              rowGap: 1,
            }}
          >
            Error:{" "}
            {txInfo.error ? txInfo.error.message : "Something went wrong"}{" "}
            {txLink}
          </Alert>
        );
      }
    }
  })();

  return (
    <Box
      textStyle="sm"
      mt={3}
      alignItems="center"
      whiteSpace="pre-wrap"
      wordBreak="break-all"
      color={txInfo.status === "error" || isErrorResult ? "error" : undefined}
    >
      {content}
    </Box>
  );
};

export default React.memo(ContractMethodResultWalletClient);
